import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GridPartial, UserApiData, UserState } from './types';
import { UserGridStateCreator } from './UserGridStateCreator';
import { AppThunk } from '../../calendar/index';
import { NOT_LOGGED_USER_DATA } from 'src/constants';
import { BaseApiResponse } from 'src/api/types';
import { fetchAndHandleCalendarData } from 'src/store/src/calendar';
import { simpleQueryServerData } from 'src/api/src/simpleQueryServerData';
import * as actionTypes from 'src/widgets/chat/constants/actions';

export const initialState: UserState = {
    grid: {
        ...new UserGridStateCreator('HALFHOUR').getGridState()
    },
    isLogged: null,
    login: null,
    firstName: null,
    lastName: null,
    adminFeatures: false,
    userID: null,
    userType: null,
    chat: false,
    todo: false,
    notes: false,
    timers: false,
    sessionID: '',
    userNameToDisplay: '',
    timeToLogout: null
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setGrid: (state, action: PayloadAction<GridPartial>) => {
            state.grid = action.payload.grid;
        },
        setLoggedOffUser: () => initialState,
        setUser: (state, action: PayloadAction<UserState>) => {
            const {
                grid,
                isLogged,
                login,
                firstName,
                lastName,
                adminFeatures,
                userID,
                userType,
                chat,
                todo,
                notes,
                timers,
                userNameToDisplay,
                sessionID,
                timeToLogout
            } = action.payload;
            state.grid = grid;
            state.isLogged = isLogged;
            state.login = login;
            state.firstName = firstName;
            state.lastName = lastName;
            state.adminFeatures = adminFeatures;
            state.userID = userID;
            state.userType = userType;
            state.chat = chat;
            state.todo = todo;
            state.notes = notes;
            state.timers = timers;
            state.userNameToDisplay = userNameToDisplay;
            state.sessionID = sessionID;
            state.timeToLogout = timeToLogout;
        }
    },
    extraReducers: {
        // normal redux reducers to be compatible with chat
        [actionTypes.SET_USER]: (state, action: PayloadAction<{ user: UserState }>) => {
            const { payload } = action;

            return {
                ...state,
                ...payload.user
            };
        }
    }
});

export const handleUserSetting = (user: UserApiData) => {
    const userToSet = {
        ...user,
        userNameToDisplay:
            user.firstName && user.lastName
                ? `${user.firstName} ${user.lastName}`
                : `${user.login}`,
        grid: {
            ...new UserGridStateCreator(user.grid ?? '').getGridState()
        }
    };

    return setUser(userToSet);
};

export const handleUserSetGrid =
    (grid: string): AppThunk =>
    async (dispatch) => {
        const payload2 = {
            grid: grid
        };
        try {
            const res: any = await simpleQueryServerData(
                'api_react/userSettings.php',
                'POST',
                payload2
            );
            if (res?.success) {
                const gridToSet = res.data?.grid ? res.data?.grid : 'HALFHOUR';
                const userToSet = {
                    grid: {
                        ...new UserGridStateCreator(gridToSet).getGridState()
                    }
                };
                dispatch(setGrid(userToSet));
                dispatch(fetchAndHandleCalendarData());
            }
        } catch (e) {
            console.log(e, 'GridSet is not working');
        }
    };

/**
 * try to set proper user data by given response from api, handle error situations, and setting
 * data when user has no acces, so is not logged
 * @param apiResponse response from api with user data
 */
export const handleUserApiDataResponse =
    (apiResponse: BaseApiResponse<UserApiData>): AppThunk =>
    async (dispatch) => {
        if (!apiResponse.success || !apiResponse.data) {
            // if errorCode 1 it is unauthorized access, so set unlogged user data
            if (apiResponse.errorCode === 1) {
                dispatch(handleUserSetting(NOT_LOGGED_USER_DATA));
            } else {
                throw new Error(apiResponse.message);
            }
        } else {
            dispatch(handleUserSetting(apiResponse.data));
        }
    };

export const { setGrid, setUser, setLoggedOffUser } = userSlice.actions;

export default userSlice.reducer;
